/**
 * Pricing Mode Helpers
 *
 * Pure utilities for pricing mode calculations.
 * For context update factories, see context-update.ts
 */

export type PricingMode = "CASH" | "INSURED";

export interface PricingEntry {
  priceData: unknown;
  mode: PricingMode;
}

export type PricingMap = Record<string, PricingEntry>;

/**
 * Determines if pricing should use INSURED mode.
 * Returns true if insurance is verified (allActive or insuranceStates.primary.active)
 * or pending prices mode is enabled.
 */
export function shouldUseInsuredPricing(context: {
  allActive?: boolean;
  insuranceStates?: { primary?: { active?: boolean } };
  pendingPricesMode?: boolean;
}): boolean {
  return (
    context.allActive === true ||
    context.insuranceStates?.primary?.active === true ||
    context.pendingPricesMode === true
  );
}

/**
 * Updates the pricing map to set INSURED mode for a specific HRT.
 * Returns a new pricing map with the updated mode.
 */
export function updatePricingMapMode(
  pricingMap: PricingMap | undefined,
  hrtId: number,
  mode: PricingMode,
): PricingMap {
  const existingMap = pricingMap ?? {};
  const existingEntry = existingMap[hrtId] as PricingEntry | undefined;

  if (!existingEntry) {
    return existingMap;
  }

  return {
    ...existingMap,
    [hrtId]: {
      ...existingEntry,
      mode,
    },
  };
}

/**
 * Determines pricing mode from a PAY event.
 * Returns "INSURED" if useInsurance is true, otherwise "CASH".
 */
export function resolvePricingModeFromEvent(eventData: {
  useInsurance?: boolean;
}): PricingMode {
  return eventData?.useInsurance ? "INSURED" : "CASH";
}
